import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { of, ReplaySubject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged, exhaustMap, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { IExternalEntityCriteria } from 'src/app/masters/peoples/external-entity/external-entity.interface';
import { Api } from 'src/app/shared/constants/api';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { SharedFormService } from 'src/app/shared/services/SharedFormService.service';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
    selector: 'app-share-weekly-consol-email',
    templateUrl: './share-weekly-consol-email.component.html',
    styleUrls: ['./share-weekly-consol-email.component.less'],
    standalone: false
})
export class ShareWeeklyConsolEmailComponent implements OnInit {

  shareWeeklyEmailForm!: FormGroup;
  customerList: any = [];
  filteredCustomers = new ReplaySubject<any[]>(1);
  customerSearchCtrl = new FormControl('');
  searching: boolean = false;
  customerSelected: boolean = false;
  emailList: any = [];
  emailsList: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private formBuilder: FormBuilder, private service: DataService,
    private utilityService: UtilityService, private logger: NGXLogger,
    private dialogRef: MatDialogRef<ShareWeeklyConsolEmailComponent>, private router: Router,
    private permissionService: PermissionService, private sharedFormService: SharedFormService,) {

  }

  ngOnInit(): void {
    this.initializeForm();
    this.getCustomerList();
    this.filterCustomer();
  }

  private initializeForm(): void {
    this.shareWeeklyEmailForm = this.formBuilder.group({
      customerId: [null, Validators.required],
      selectedEmails: [[]],
      enteredEmails: [null, Validators.required],
      planningOrientation: ['HORIZONTAL', Validators.required]
    });
  }

  getCustomerList() {
    let criteria: IExternalEntityCriteria = {
      activeFlag: true,
      domicileType: 'DOMESTIC',
      customerFlag: true,
      // id:this.customerId?.value,
    }
    this.logger.info("Criteria to fetch customers", criteria);
    this.service.get(Api.EXTERNAL_ENTITIES_FOR_DD, criteria).subscribe(res => {
      if (res) {
        this.logger.info("Fetched Customers List", res);
        this.customerList = res.contents;
        this.filteredCustomers.next(this.customerList);
      }
    })
  }

  filterCustomer() {
    this.customerSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (!value) {
          this.filteredCustomers.next([...this.customerList]);
          return of(this.customerList);
        }

        return this.service.get(Api.EXTERNAL_ENTITIES_FOR_DD, {
          name: value,
          activeFlag: true,
          domicileType: 'DOMESTIC',
          customerFlag: true,
        }).pipe(
          map(res => res.contents || []),
          catchError(() => {
            this.searching = false;
            return of([...this.customerList]);
          })
        );
      })
    ).subscribe(results => {
      this.filteredCustomers.next(results);
      this.searching = false;
    });
  }

  setCustomerValueChangeEvent(value?: number) {
    if (!value) return;
    this.customerSelected = true;
    this.filteredCustomers.pipe(take(1)).subscribe(list => {
      const selectedCustomer =
        this.customerList.find(c => c.id === value) ||
        list.find(c => c.id === value);

      if (selectedCustomer && !this.customerList.some(c => c.id === selectedCustomer.id)) {
        this.customerList.push(selectedCustomer);
      }

      this.logger.info('id of the customer', value);
      this.service.get(Api.EXTERNAL_ENTITY_EMAILS_AND_PHONE, { id: value }).subscribe(res => {
        this.logger.info("Customer Emails", res);
        this.emailList = res?.[0]?.emailIds || [];
      });
    });
  }

  get enteredEmails(): FormControl {
    return this.shareWeeklyEmailForm.get('enteredEmails') as FormControl;
  }

  private updateEnteredEmailsControl(): void {
    this.enteredEmails.setValue(this.emailsList.length > 0 ? this.emailsList : null);
  }

  onEmailSelectChange(selectedEmails: string[]): void {
    selectedEmails.forEach(email => {
      if (!this.emailExists(email)) {
        this.emailsList.push(email);
      }
    });
    this.updateEnteredEmailsControl();
    this.logger.info("Selected Emails", this.emailsList);
  }

  emailExists(email: string): boolean {
    return this.emailsList.includes(email);
  }

  addEmail(event: MatChipInputEvent, input: HTMLInputElement): void {
    const value = (event.value || '').trim();
    input.value = '';

    if (!value) return;

    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,}$/i;

    if (!emailRegex.test(value) || this.emailExists(value)) {
      return;
    }

    this.emailsList.push(value);
    this.updateEnteredEmailsControl();

    // also reflect in dropdown
    const selected = this.shareWeeklyEmailForm.get('selectedEmails')?.value || [];
    this.shareWeeklyEmailForm.patchValue({
      selectedEmails: [...selected, value]
    });
  }

  removeEmailByIndex(index: number): void {
    const email = this.emailsList[index];
    this.emailsList.splice(index, 1);
    this.updateEnteredEmailsControl();

    const selected = this.shareWeeklyEmailForm.get('selectedEmails')?.value || [];
    this.shareWeeklyEmailForm.patchValue({
      selectedEmails: selected.filter((e: string) => e !== email)
    });
  }

  private buildFinalEmailPayload() {
    return {
      customerId: this.shareWeeklyEmailForm.get('customerId')?.value,
      customerName: this.customerList.find(c=> c.id == this.shareWeeklyEmailForm.get('customerId')?.value).name,
      emails: this.emailsList,
      // planningContext: this.data.planningContext,
      planningContext: {
        ...this.data.planningContext,
        planningOrientation: this.shareWeeklyEmailForm.get('planningOrientation')?.value
      },
      planningDetails: this.data.planningDetails
    };
  }

  sendEmail(): void {
    if (!this.customerSelected) {
      return;
    }
    this.shareWeeklyEmailForm.markAllAsTouched();
    if (this.shareWeeklyEmailForm.invalid) {
      return;
    }

    this.logger.info("Planning Context",this.data.planningContext);
    this.logger.info("Planning Details",this.data.planningDetails);

    const payload = this.buildFinalEmailPayload();
    this.logger.info("Weekly Data for Sending Mail",payload);
    this.service.post(Api.SHARE_WEEKLY_PLANNING_EMAIL, payload)
      .subscribe(res =>{
        if(res){
          this.logger.info('Weekly planning email sent');
          this.utilityService.toastrSuccessMessage("Email Sent Sucessfully");
          this.dialogRef.close(true);
        }
      });

  }

}
